import React from 'react';
import { Link } from 'react-router-dom';
import { getArticleDisplayStatus, getArticleStatusText } from '../utils/articleStatus';
import { Article } from '../types';
import { formatDateGerman, formatDateTimeGerman } from '../utils/dateFormatter';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { Button } from './ui/Button';
import ArticleImage from './ArticleImage';
import StatusBadge from './ui/StatusBadge';
import ArticleTypeBadge from './ui/ArticleTypeBadge';
import { publishedArticleUrl } from '../utils/publishedArticleUrl';
import { 
  UserIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

type ArticleCardVariant = 'articleList' | 'allArticles' | 'dashboard';

interface ArticleCardProps {
  article: Article;
  variant: ArticleCardVariant;
  authorName?: string;
  onDelete?: (articleId: string, articleTitle: string) => void;
  deleting?: boolean;
  onReschedule?: (article: Article) => void;
  formatScheduled?: (date: string) => string;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  variant,
  authorName,
  onDelete,
  deleting = false,
  onReschedule,
  formatScheduled
}) => {
  const isAudio = article.type === 'audio';
  const publicUrl = article.status === 'published' || article.publicationState === 'accepted'
    ? publishedArticleUrl(article.publishedUrl) || publishedArticleUrl(article.publicationCandidateUrl) : undefined;
  const showHover = article.type === 'text';
  const linkLabel = `Artikel ${article.title} öffnen`;

  const containerClassName = (() => {
    if (variant === 'articleList') {
      return `relative flex flex-col gap-4 sm:flex-row sm:items-stretch sm:gap-6 p-4 border border-gray-200 rounded-lg transition-colors ${
        showHover ? 'hover:bg-gray-50 cursor-pointer' : ''
      }`;
    }
    if (variant === 'allArticles') {
      return 'relative flex flex-col md:flex-row md:items-stretch md:space-x-6 p-4 border border-gray-200 rounded-lg transition-colors hover:bg-gray-50';
    }
    return `relative flex flex-col md:flex-row md:items-stretch md:space-x-6 p-4 border border-gray-200 rounded-lg transition-colors ${
      showHover ? 'hover:bg-gray-50 cursor-pointer' : ''
    }`;
  })();

  const renderTypeBadge = (size: 'sm' | 'md' | 'lg' = 'sm', className = '') => (
    <ArticleTypeBadge type={article.type} size={size} className={className} />
  );

  const renderStatusBadge = (size: 'sm' | 'md' | 'lg' = 'sm', className = '') => (
    <StatusBadge status={getArticleDisplayStatus(article)} label={getArticleStatusText(article)} size={size} className={className} />
  );

  const renderImage = () => (
    <ArticleImage
      imageUrl={article.imageStorageUrl || undefined}
      alt={article.title || 'Artikelbild'}
      variant={variant}
      isAudio={isAudio}
    />
  );

  const renderTextContent = () => {
    if (variant === 'allArticles') {
      return (
        <>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
            {article.title || 'Ohne Titel'}
          </h3>
          {article.teaser && (
            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
              {article.teaser}
            </p>
          )}
        </>
      );
    }

    return (
      <>
        {article.topic && (
          <div className="mb-1">
            <p className="text-sm text-gray-900">{article.topic}</p>
          </div>
        )}
        <div className="mb-2">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">
            {article.title || 'Ohne Titel'}
          </h3>
          {article.subtitle && (
            <p className="text-sm text-gray-900">
              {article.subtitle}
            </p>
          )}
        </div>
      </>
    );
  };

  const renderAudioContent = () => {
    if (variant === 'allArticles') {
      return (
        <>
          {article.originalArticleTitle && (
            <div className="mb-2">
              <p className="text-sm text-gray-500">
                Original: <a href={article.originalArticleUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {article.originalArticleTitle}
                </a>
              </p>
            </div>
          )}
          {article.audioStorageUrl && (
            <div className="mt-2 space-y-2">
              <audio controls src={article.audioStorageUrl} className="w-full" />
              <a
                href={article.audioStorageUrl}
                download
                className="text-sm text-blue-600 hover:underline inline-flex items-center"
              >
                Audio herunterladen
              </a>
            </div>
          )}
        </>
      );
    }

    return (
      <>
        {article.originalArticleTitle && (
          <div className="mb-2">
            <p className="text-sm text-gray-500">
              Original: <a href={article.originalArticleUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {article.originalArticleTitle}
              </a>
            </p>
          </div>
        )}
      </>
    );
  };

  const renderMetadata = () => {
    if (variant === 'allArticles') {
      return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
          <span>Erstellt: {formatDateGerman(article.createdAt)}</span>
          {article.status !== 'published' && article.scheduledAt && formatScheduled && (
            <span>Geplant: {formatScheduled(article.scheduledAt)}</span>
          )}
          {article.status === 'published' && article.publishedAt && (
            <span>Veröffentlicht: {formatDateTimeGerman(article.publishedAt)} Uhr</span>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
        <div className="flex items-center">
          <CalendarIcon className="w-3 h-3 mr-1" />
          <span>Erstellt: {formatDateGerman(article.createdAt)}</span>
        </div>
        {article.status !== 'published' && article.scheduledAt && formatScheduled && (
          <div className="flex items-center">
            <ClockIcon className="w-3 h-3 mr-1" />
            {variant === 'articleList' && onReschedule ? (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onReschedule(article);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline cursor-pointer transition-colors relative flex items-center gap-1"
              >
                <span>Geplant: {formatScheduled(article.scheduledAt)}</span>
                <CalendarIcon className="w-3.5 h-3.5" />
              </button>
            ) : (
              <span>Geplant: {formatScheduled(article.scheduledAt)}</span>
            )}
          </div>
        )}
        {article.status === 'published' && article.publishedAt && (
          <div className="flex items-center">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            <span>Veröffentlicht: {formatDateTimeGerman(article.publishedAt)} Uhr</span>
          </div>
        )}
      </div>
    );
  };

  const renderHeader = () => {
    if (variant === 'articleList') {
      return (
        <div className="flex items-center mb-1">
          {renderTypeBadge('sm')}
        </div>
      );
    }

    if (variant === 'allArticles') {
      return (
        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2 gap-2">
          <div className="flex items-center">
            <UserIcon className="w-4 h-4 mr-2 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">{authorName || 'Lädt...'}</span>
          </div>
          <div className="flex flex-col gap-1 md:items-end">
            {renderTypeBadge('sm', 'w-fit')}
            {renderStatusBadge('sm', 'w-fit')}
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(article.id, article.title || 'Unbekannter Titel');
                }}
                disabled={deleting}
                className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
              >
                {deleting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <TrashIcon className="w-4 h-4" />
                )}
                <span className="ml-1">
                  {deleting ? 'Löschen...' : 'Löschen'}
                </span>
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2 gap-2">
        <div className="flex items-center">
          <UserIcon className="w-4 h-4 mr-2 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">{authorName || 'Lädt...'}</span>
        </div>
        <div className="flex flex-row md:flex-col gap-2 md:gap-1 md:items-end">
          {renderTypeBadge('sm')}
          {renderStatusBadge('sm', 'w-fit')}
        </div>
      </div>
    );
  };

  return (
    <div className={containerClassName}>
      {renderImage()}
      <div className="flex-1 min-w-0">
        {renderHeader()}
        {isAudio ? renderAudioContent() : renderTextContent()}
        {renderMetadata()}
        {publicUrl && <a href={publicUrl} target="_blank" rel="noopener noreferrer"
          onClick={event => event.stopPropagation()}
          className="relative z-10 mt-3 inline-flex items-center text-sm font-medium text-blue-700 hover:underline">
          {article.status === 'published' ? 'Veröffentlichten Artikel öffnen ↗' : 'Eingereichten Artikel öffnen ↗'}
        </a>}
      </div>
      {variant === 'articleList' && (
        <div className="flex items-center">
          {renderStatusBadge('md')}
          {article.publicationState === 'uncertain' && article.status !== 'published' && <span className="text-xs text-amber-800">Übergabe unbestätigt – Publikationsdienst prüfen</span>}
        </div>
      )}
      {variant === 'allArticles' ? (
        <>
          <Link 
            to={`/articles/${article.id}`}
            className="absolute inset-0 pointer-events-none"
            aria-label={`Artikel "${article.title}" öffnen`}
          />
          <div 
            className="absolute inset-0 cursor-pointer"
            style={{ 
              clipPath: 'polygon(0 0, calc(100% - 120px) 0, calc(100% - 120px) 100%, 0 100%)' 
            }}
            onClick={() => window.location.href = `/articles/${article.id}`}
          />
        </>
      ) : (
        <Link
          to={`/articles/${article.id}`}
          className="absolute inset-0"
          aria-label={linkLabel}
        />
      )}
    </div>
  );
};

export default ArticleCard;
